// Quarterly budgets and their lines. Money is ALWAYS integer ngwee here.
//
// The approval rules (who may submit, approve, reopen; what happens under each approval
// mode) live in the database (0010_approval_engine.sql). These functions just ask; the
// database answers, and its plain-language refusals are shown to the person as written.
import { supabase } from '@/lib/supabase'
import { currentUserId } from './auth'
import { DataError, unwrap } from './errors'

export type BudgetStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'withdrawn'

export interface Category {
  id: string
  name: string
}

export async function listCategories(): Promise<Category[]> {
  const res = await supabase
    .from('expense_category')
    .select('id, name')
    .is('archived_at', null)
    .order('sort_order')
    .order('name')
  return unwrap(res as never, 'Could not load the spending categories.') as Category[]
}

export interface Budget {
  id: string
  departmentId: string
  quarterId: string
  status: BudgetStatus
  requiresApproval: boolean | null
  approvedVia: 'central' | 'auto_policy' | 'auto_on_policy_change' | null
  submittedAt: string | null
  decidedAt: string | null
  decisionNote: string | null
  revision: number
  note: string | null
  /** Sum of its lines. */
  totalNgwee: number
  lineCount: number
}

type BudgetRow = {
  id: string
  department_id: string
  quarter_id: string
  status: BudgetStatus
  requires_approval: boolean | null
  approved_via: Budget['approvedVia']
  submitted_at: string | null
  decided_at: string | null
  decision_note: string | null
  revision: number
  note: string | null
  lines: { amount_ngwee: number }[]
}

const BUDGET_COLS =
  'id, department_id, quarter_id, status, requires_approval, approved_via, submitted_at, decided_at, decision_note, revision, note, lines:budget_line(amount_ngwee)'

function toBudget(r: BudgetRow): Budget {
  return {
    id: r.id,
    departmentId: r.department_id,
    quarterId: r.quarter_id,
    status: r.status,
    requiresApproval: r.requires_approval,
    approvedVia: r.approved_via,
    submittedAt: r.submitted_at,
    decidedAt: r.decided_at,
    decisionNote: r.decision_note,
    revision: r.revision,
    note: r.note,
    totalNgwee: (r.lines ?? []).reduce((s, l) => s + Number(l.amount_ngwee), 0),
    lineCount: (r.lines ?? []).length,
  }
}

/** Every budget of the department, newest quarter first. */
export async function listBudgets(departmentId: string): Promise<Budget[]> {
  const res = await supabase
    .from('budget')
    .select(BUDGET_COLS)
    .eq('department_id', departmentId)
    .order('quarter_id', { ascending: false })
  return (unwrap(res as never, 'Could not load the budgets.') as BudgetRow[]).map(toBudget)
}

/** The budget for one quarter, or null when none has been started. */
export async function getBudget(departmentId: string, quarterId: string): Promise<Budget | null> {
  const res = await supabase
    .from('budget')
    .select(BUDGET_COLS)
    .eq('department_id', departmentId)
    .eq('quarter_id', quarterId)
    .maybeSingle()
  const row = unwrap(res as never, 'Could not load the budget.') as BudgetRow | null
  return row ? toBudget(row) : null
}

export interface QuarterChoice {
  id: string
  startsOn: string
  endsOn: string
}

/** Open quarters a budget can still be started for. */
export async function listOpenQuarters(fromQuarterId: string): Promise<QuarterChoice[]> {
  const res = await supabase
    .from('fiscal_quarter')
    .select('id, starts_on, ends_on')
    .eq('status', 'open')
    .gte('id', fromQuarterId)
    .order('id')
    .limit(6)
  return (unwrap(res as never, 'Could not load the quarters.') as { id: string; starts_on: string; ends_on: string }[]).map(
    (q) => ({ id: q.id, startsOn: q.starts_on, endsOn: q.ends_on }),
  )
}

export async function startBudget(departmentId: string, quarterId: string): Promise<void> {
  const userId = await currentUserId()
  const res = await supabase
    .from('budget')
    .insert({ department_id: departmentId, quarter_id: quarterId, created_by: userId })
  if (res.error) throw new DataError(res.error, 'Could not start the budget.')
}

/** Throw away a budget that is still a draft (the database refuses anything else). */
export async function discardDraftBudget(budgetId: string): Promise<void> {
  const res = await supabase.from('budget').delete().eq('id', budgetId).eq('status', 'draft').select('id')
  if (res.error) throw new DataError(res.error, 'Could not discard the draft.')
  if (((res.data as unknown[]) ?? []).length === 0) {
    throw new DataError({ code: 'P0001', message: 'This budget has changed since you opened it, so it cannot be discarded. Refresh the page.' })
  }
}

// ---------------------------------------------------------------- lines
export interface BudgetLine {
  id: string
  budgetId: string
  categoryId: string
  categoryName: string
  description: string
  amountNgwee: number
  note: string | null
}

type LineRow = {
  id: string
  budget_id: string
  category_id: string
  description: string
  amount_ngwee: number
  note: string | null
  category: { name: string } | null
}

export async function listBudgetLines(budgetId: string): Promise<BudgetLine[]> {
  const res = await supabase
    .from('budget_line')
    .select('id, budget_id, category_id, description, amount_ngwee, note, category:expense_category(name)')
    .eq('budget_id', budgetId)
    .order('created_at')
  return (unwrap(res as never, 'Could not load the budget lines.') as LineRow[]).map((r) => ({
    id: r.id,
    budgetId: r.budget_id,
    categoryId: r.category_id,
    description: r.description,
    amountNgwee: Number(r.amount_ngwee),
    note: r.note,
    categoryName: r.category?.name ?? '',
  }))
}

export interface LineInput {
  categoryId: string
  description: string
  amountNgwee: number
  note?: string
}

/**
 * Add a line. `clientRef` (one per form) makes a retry whose first answer was lost find the line it
 * already made, with the corrected details applied, instead of adding a second.
 */
export async function addBudgetLine(budget: { id: string; departmentId: string }, l: LineInput, clientRef?: string): Promise<void> {
  const fields = {
    category_id: l.categoryId,
    description: l.description.trim(),
    amount_ngwee: l.amountNgwee,
    note: l.note?.trim() ? l.note.trim() : null,
  }
  const res = await supabase.from('budget_line').insert({
    budget_id: budget.id,
    department_id: budget.departmentId,
    ...fields,
    ...(clientRef ? { client_ref: clientRef } : {}),
  })
  if (res.error) {
    if (res.error.code === '23505' && clientRef) {
      const prior = await supabase.from('budget_line').select('id').eq('budget_id', budget.id).eq('client_ref', clientRef).maybeSingle()
      if (prior.data) {
        await supabase.from('budget_line').update(fields).eq('id', (prior.data as { id: string }).id)
        return
      }
    }
    throw new DataError(res.error, 'Could not save the line.')
  }
}

export async function updateBudgetLine(lineId: string, l: LineInput): Promise<void> {
  const res = await supabase
    .from('budget_line')
    .update({
      category_id: l.categoryId,
      description: l.description.trim(),
      amount_ngwee: l.amountNgwee,
      note: l.note?.trim() ? l.note.trim() : null,
    })
    .eq('id', lineId)
  if (res.error) throw new DataError(res.error, 'Could not save the line.')
}

export async function removeBudgetLine(lineId: string): Promise<void> {
  const res = await supabase.from('budget_line').delete().eq('id', lineId).select('id')
  if (res.error) throw new DataError(res.error, 'Could not remove the line.')
  if (((res.data as unknown[]) ?? []).length === 0) {
    throw new DataError({ code: 'P0001', message: 'That line is no longer there, or the budget can no longer be changed. Refresh the page.' })
  }
}

// ---------------------------------------------------------------- status changes
async function setStatus(
  budgetId: string,
  from: BudgetStatus[],
  patch: { status: BudgetStatus; decision_note?: string },
  fallback: string,
): Promise<void> {
  // `.in('status', from)` plus a returned row turns "someone else already changed it"
  // into a clear message instead of a silent no-op.
  const res = await supabase.from('budget').update(patch).eq('id', budgetId).in('status', from).select('id')
  if (res.error) throw new DataError(res.error, fallback)
  if (((res.data as unknown[]) ?? []).length === 0) {
    throw new DataError(
      { code: 'P0001', message: 'This budget has changed since you opened it. Refresh the page and try again.' },
      fallback,
    )
  }
}

export const submitBudget = (id: string) =>
  setStatus(id, ['draft'], { status: 'submitted' }, 'Could not submit the budget.')
export const withdrawBudget = (id: string) =>
  setStatus(id, ['submitted'], { status: 'withdrawn' }, 'Could not take the budget back.')
/** After a rejection or a withdrawal: back to a draft that can be edited and sent again. */
export const reopenAsDraft = (id: string) =>
  setStatus(id, ['rejected', 'withdrawn'], { status: 'draft' }, 'Could not reopen the budget.')
export const approveBudget = (id: string) =>
  setStatus(id, ['submitted'], { status: 'approved' }, 'Could not approve the budget.')
export const rejectBudget = (id: string, reason: string) =>
  setStatus(id, ['submitted'], { status: 'rejected', decision_note: reason.trim() }, 'Could not reject the budget.')
/** Central treasury only: formally reopen an APPROVED budget (reason required, audited). */
export const reviseApprovedBudget = (id: string, reason: string) =>
  setStatus(id, ['approved'], { status: 'draft', decision_note: reason.trim() }, 'Could not reopen the budget.')

export interface WaitingBudget extends Budget {
  departmentName: string
}

/** Budgets waiting for a decision, across the church (central treasury's queue), oldest first. */
export async function listWaitingBudgets(): Promise<WaitingBudget[]> {
  const res = await supabase
    .from('budget')
    .select(`${BUDGET_COLS}, department:department(name)`)
    .eq('status', 'submitted')
    .order('submitted_at')
  return (unwrap(res as never, 'Could not load the budgets waiting for approval.') as (BudgetRow & { department: { name: string } | null })[]).map(
    (r) => ({ ...toBudget(r), departmentName: r.department?.name ?? '' }),
  )
}

export interface LineActual {
  lineId: string
  budgetedNgwee: number
  /** Paid out (net of any reversals). */
  actualNgwee: number
  /** Sent or approved but not yet paid. */
  committedNgwee: number
  /** Budgeted minus paid minus committed; negative means over budget. */
  remainingNgwee: number
}

/** Planned vs spent for each line of one budget (only meaningful once it is approved). */
export async function listBudgetActuals(budgetId: string): Promise<LineActual[]> {
  const res = await supabase
    .from('v_budget_line_actuals')
    .select('budget_line_id, budgeted_ngwee, actual_ngwee, committed_ngwee, remaining_ngwee')
    .eq('budget_id', budgetId)
  return (
    unwrap(res as never, 'Could not load what has been spent.') as {
      budget_line_id: string
      budgeted_ngwee: number
      actual_ngwee: number
      committed_ngwee: number
      remaining_ngwee: number
    }[]
  ).map((r) => ({
    lineId: r.budget_line_id,
    budgetedNgwee: Number(r.budgeted_ngwee),
    actualNgwee: Number(r.actual_ngwee),
    committedNgwee: Number(r.committed_ngwee),
    remainingNgwee: Number(r.remaining_ngwee),
  }))
}
